import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import template from './transformation-form.hbs'
import TransformationIf from './transformation-if'
import TransformationThen from './transformation-then'
import FormView from 'scoreboard/components/form-view'
import i18n from 'scoreboard/components/i18n'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import DataPreviewPanel from 'scoreboard/components/data-preview-panel'
import loadingDots from 'scoreboard/components/loading-dots.hbs'
import * as urlToLinks from '../../components/urls-to-links'
import DataType from 'scoreboard/datasets/model/data-type'
const { TIME, DATE_TIME } = DataType

var TransformationForm = Backbone.View.extend({

	/**
	 * Form for modifying a single transformation applied to data before it is
	 * imported into Scoreboard. Used by datasets, metric value imports and
	 * initiative status imports.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Importable} options.model
	 *
	 * @param {Object} [options.transformation] the transformation being edited,
	 * if applicable
	 *
	 * @param {Array} options.fields the fields which must have id and label
	 * properties, and may optionally have a dataTypes property. The id property
	 * can be a String if needed (e.g. for datasets)
	 *
	 * @param {Map<String, Object>} [options.formattingOptionsByFieldRawColumn]
	 * formatting options for basic fields, keyed by the field's raw column name
	 */
	initialize: function(options = {}) {
		this.options = options
		let MyFormView = FormView.extend({
			test: this.test.bind(this)
		})
		this.formView = new MyFormView({
			el: this.el,
			submit: this.submit.bind(this),
			// if the form validates without any changes, that's fine
			allowCleanSubmission: true
		})
		this.transformationThens = []
		// #30619 get initial value of transformation if fieldId, if any,
		// to allow comparisons in onChangeIfField
		this.oldIfFieldId = _.get(this.options.transformation, 'if.fieldId')
		this.dataTypesByFieldLabel = this.getDataTypesByFieldLabel()
		this.formattingOptionsByFieldRawColumn = this.options?.formattingOptionsByFieldRawColumn || {}
	},

	tagName: 'form',
	className: 'transformation-form',

	events: {
		'change .transformation-if [name=fieldId]': 'onChangeIfField',
		'change .transformation-if [name=operator]': 'onChangeIfOperator',
		'click .add-then': 'onAddThen',
		'click .delete': 'onDeleteThen'
	},

	render: function() {
		this.$el.html(template(this.context()))

		this.renderBanner()
		this.renderTransformationIf()
		this.renderTransformationThens()
		this.updatePreview()

		this.formView.start()
		this.formView.on('change', () => this.updatePreview())
		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })

		return this
	},

	renderBanner: function() {
		// #30668 message with link to support article
		let href = 'https://support.spiderstrategies.com/hc/en-us/articles/360041132552-Translating-Data-While-Importing'
		let link = urlToLinks.createAnchor(href,
			'admin.import.transform.instructions.supportArticle')
		let key = 'admin.import.transform.instructions'
		// #29852 add instructions banner for KPI imports
		if (_.get(this.model.attributes, 'type') === 'metric-value') {
			key += '.metricValue'
		}
		let html = i18n.get(key, link)

		// #30668 #30673 display additional instructions with link to support
		// article if 'matches regular expression' is chosen
		let operator = this.$('.transformation-if [name=operator]').val()
		if (operator === 'matches-regular-expression') {
			let regExHref = href + '#regex'
			let regExLink = urlToLinks.createAnchor(regExHref,
				'admin.import.transform.regularExpression.supportArticle')
			let regExMessage = i18n.get(
				'admin.import.transform.regularExpression.instructions', regExLink)
			html += `<p> ${regExMessage}</p>`
		}

		this.$('.banner-description').empty().html(html)
	},

	renderTransformationIf: function() {
		this.transformationIf = new TransformationIf({
			model: this.model,
			fields: this.ifFields(this.options.fields),
			groupBy: this.options.groupBy,
			transformation: this.options.transformation,
			formView: this.formView,
			formattingOptionsByFieldRawColumn: this.formattingOptionsByFieldRawColumn
		})
		this.$('.transformation-if-container').html(this.transformationIf.el)
		this.transformationIf.render()
		this.transformationIf.on('change:values', () => this.updatePreview())
	},

	renderTransformationThens: function() {
		let thens = _.get(this.options.transformation, 'thens')
		_.each(thens, this.renderTransformationThen.bind(this))
		if (_.isEmpty(thens)) {
			this.renderTransformationThen()
		}
		this.toggleDeleteThen()
	},

	renderTransformationThen: function(then) {
		then = then || {}
		let fields = this.thenFields()

		let transformationThen = new TransformationThen({
			fields,
			groupBy: this.options.groupBy,
			transformation: this.options.transformation,
			then,
			formView: this.formView,
			type: _.get(this.model.attributes, 'type')
		})
		this.$('.transformation-then-container').append(transformationThen.el)
		transformationThen.render()
		this.transformationThens.push(transformationThen)
	},

	fieldsForType: function() {
		// for initiative-status value imports, we want filter out the fields
		// from vi-transformations.js that only apply to metric value imports
		let type = _.get(this.model.attributes, 'type')
		let fields = _.cloneDeep(this.options.fields)
		if (type === 'initiative-status') {
			fields =  _.filter(fields, f => f.type != 'metric-value')
		}
		return fields
	},

	ifFields: function () {
		let fields = this.fieldsForType()
		// filter out fields that should only appear for the Then fields
		const ifFields = _.filter(fields, f => f.display != 'thenOnly')
		// TODO Remove this with #34255; implement time/datetime transforms
		const ifFieldsSansTimeAndDatetime = ifFields.filter(it => {
			return !_.includes([TIME, DATE_TIME], it.dataType)
		})
		return ifFieldsSansTimeAndDatetime
	},

	thenFields: function() {
		let fields = this.fieldsForType()
		// filter out fields that should only appear for the If fieldId
		let thenFields = _.filter(fields, f => f.display != 'ifOnly')
		// "each of these values" should only be present if
		// 'any-kpi-or-threshold-value' is selected
		if (this.transformationIf.getFieldId() != 'any-kpi-or-threshold-value') {
			thenFields = _.reject(thenFields, f => f.id === 'each-of-these-values')
		}
		thenFields = this.filterFieldsByOperator(thenFields)
		// TODO Remove this with #34255; implement time/datetime transforms
		const thenFieldsSansTimeAndDatetime = thenFields.filter(it => {
			return !_.includes([TIME, DATE_TIME], it.dataType)
		})
		return thenFieldsSansTimeAndDatetime
	},

	getTransformOperator() {
		return this.$('.transformation-if [name=operator]').val()
	},

	/**
	 * @returns {Map<String, String>}  a map of dataTypes keyed by field
	 * labels (for the fields that have dataTypes associated with them)
	 * Used for determining dataTypes in the data-preview-panel if needed
	 */
	getDataTypesByFieldLabel: function() {
		let fields = _.cloneDeep(this.options.fields)
		fields = _.filter(fields, f => !_.isNil(f.dataType))
		let dataTypesByFieldLabel = new Map(fields.map(field =>
			[_.get(field, 'label'), _.get(field, 'dataType')]))
		return dataTypesByFieldLabel
	},

	/**
	 * If a field has the 'forOperators' property set, hide them if the currently
	 * selected operator isn't in the list of acceptable options
	 */
	filterFieldsByOperator: function(thenFields) {
		const operator = this.getTransformOperator()
		return _.filter(thenFields, f => !f.forOperators || f.forOperators.includes(operator))
	},

	context: function() {
		let existingTransformation = _.get(this.options, 'transformation.id')
		let key = existingTransformation ? 'common.edit2' : 'common.add2'
		let title = i18n.get(key, 'admin.import.transform.transformation')
		return { title }
	},

	// if there is only one "THEN", don't allow it to be deleted
	toggleDeleteThen: function() {
		let showThen = this.$('.transformation-then').length > 1
		this.$('.transformation-then').toggleClass('is-deletable', showThen)
	},

	onChangeIfField: function() {
		let ifFieldId = this.transformationIf.getFieldId()
		// if 'any-kpi-or-threshold-value' is selected or being un-selected,
		// re-render the then field selects for the updated inclusion of
		// "each of these values"
		if (ifFieldId === 'any-kpi-or-threshold-value' ||
			this.oldIfFieldId === 'any-kpi-or-threshold-value') {
			// re-render then field select
			_.each(this.transformationThens, then => {
				then.renderFieldSelect(this.thenFields(), then.getFieldId())
			})
		}
		this.oldIfFieldId = ifFieldId
	},

	onChangeIfOperator: function() {
		this.renderBanner()

		// #35006 if the operator changes, we need to update the 'then' fields
		// to show appropriate options
		_.each(this.transformationThens, then => {
			then.renderFieldSelect(this.thenFields(), then.getFieldId())
		})
	},

	onAddThen: function() {
		this.renderTransformationThen()
		this.toggleDeleteThen()
	},

	onDeleteThen: function(e) {
		let index = this.$(e.target).closest('.transformation-then').index()
		// #56775 before removing the deleted 'then', get any validation errors
		// by using its test method
		let errors = this.transformationThens[index]?.test()
		this.transformationThens[index].remove()
		this.transformationThens.splice(index, 1)
		this.toggleDeleteThen()
		// #36063 update the preview if a `then` is deleted
		// #56775 but only if the transformation-then that is being deleted
		// was actually valid and deleting it would change the preview
		if (_.isNil(errors)) {
			this.updatePreview()
		}
	},

	test: function() {
		let msg = this.transformationIf.test()
		if (msg) return msg

		return FormView.prototype.test.apply(this.formView, arguments)
	},

	/**
	 * Returns the data that should be submitted to the server
	 */
	readFromDom: function() {
		let thens = _.map(this.transformationThens, transformationThen => {
			return transformationThen.readFromDom()
		})
		let transformation = {
			'if': this.transformationIf.readFromDom(),
			thens
		}
		let id = _.get(this.options.transformation, 'id')
		if (id) {
			transformation.id = id
		}
		return transformation
	},

	updatePreview: function() {
		let started = this.formView && this.formView.started
		// #30529 only validate if formView has started
		if (started && !_.isNil(this.test())) {
			// #36063 not all fields are filled in, so clear out the preview
			this.updatePreviewPanel()
			return
		}
		// #31642 don't attempt to generate preview if source data not available
		let messageCode = this.model.getDataSourceMetadata('messageCode')
		if (messageCode) {
			return
		}
		// #30529 if formView hasn't started yet (initial render), use
		// this.options.transformation for initial preview
		let transformation = _.get(this.options, 'transformation', {})
		if (started) {
			transformation = this.readFromDom()
		}

		let request = {
			importable: this.model.attributes,
			transformation
		}

		let dataPreviewPanel = this.$('.data-preview-panel')
		let panelContent = dataPreviewPanel.find('.panel-content')
		// #31029 maintain the size of the preview panel while loading dots
		// are being displayed
		let table = dataPreviewPanel.find('.table')
		let height = table.height() || panelContent.height()
		let width = table.width() || panelContent.width()
		panelContent.height(height).width(width)

		dataPreviewPanel.addClass('is-loading')
		panelContent.append(loadingDots())

		$.post({
			url: this.model.getTransformationPreviewUrl(),
			data: JSON.stringify(request),
			success: example => {
				this.formView && this.formView.clearErrors()
				dataPreviewPanel.removeClass('is-loading').remove('.loading-dots')
				let columns = _.map(this.$('[name=fieldId]'), el =>
					this.$(el).val())
				this.updatePreviewPanel(example, columns)
				this.trigger('resize')
			},
			error: (xhr) => {
				this.formView.error(xhr)
			}
		})
		this.trigger('resize')
	},

	/**
	 * #36063 Populates the preview panel with examples of the transformations.
	 * Don't pass in any arguments to clear the panel
	 */
	updatePreviewPanel(example = {}, columns = []) {
		let dataPreviewPanel = this.$('.data-preview-panel')
		if (!this.dataPreviewPanel) {
			this.dataPreviewPanel = new DataPreviewPanel({
				label: i18n.get('admin.import.transform.example'),
				el: dataPreviewPanel.empty(),
				preview: example,
				columns,
				tooltipClasses: 'tooltip',
				formatValues: _.get(this.options, 'formatValues', true),
				dataTypesByColumnLabel: this.dataTypesByFieldLabel,
				formattingOptionsByFieldRawColumn: this.formattingOptionsByFieldRawColumn
			}).render()
		} else {
			this.dataPreviewPanel.render({
				preview: example,
				columns
			})
		}
	},

	submit: function() {
		this.trigger('done', this.readFromDom())
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.transformationIf, ...this.transformationThens,
			this.formView, this.covertButtonTooltips, this.dataPreviewPanel],
			'remove')
	}

})

export default TransformationForm

TransformationForm.__filename__ = 'imports/transform/transformation-form.js'
